import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import { z } from "zod";
import { MarketingShell } from "@/components/MarketingShell";
import { PageContainer } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Download, CheckCircle2 } from "lucide-react";

const searchSchema = z.object({
  file: z.string().min(1).optional(),
  email: z.string().email().optional(),
});

export const Route = createFileRoute("/lead-magnet/thanks")({
  validateSearch: (s) => {
    const parsed = searchSchema.safeParse(s);
    return parsed.success ? parsed.data : {};
  },
  head: () => ({
    meta: [
      { title: "Your download is ready — MissionReady" },
      { name: "description", content: "Download your free security ops checklist." },
      { property: "og:title", content: "Your download is ready — MissionReady" },
      { property: "og:description", content: "Download your free MissionReady security operations checklist." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: ThanksPage,
});

function ThanksPage() {
  const { file, email } = Route.useSearch();
  const linkRef = useRef<HTMLAnchorElement>(null);

  // Auto-trigger the download once on mount.
  useEffect(() => {
    if (file && linkRef.current) linkRef.current.click();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file]);


  return (
    <MarketingShell>
      <PageContainer className="py-16 md:py-24">
        <div className="mx-auto max-w-2xl text-center">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary">
            <CheckCircle2 className="h-7 w-7" />
          </div>
          <h1 className="mt-5 text-3xl font-bold tracking-tight md:text-4xl">
            Your checklist is on the way
          </h1>
          <p className="mt-3 text-muted-foreground">
            {email ? (
              <>
                We just emailed a copy to <span className="font-medium text-foreground">{email}</span>. Your download
                should also start automatically.
              </>
            ) : (
              <>Your download should start automatically. We also emailed a copy.</>
            )}
          </p>

          <div className="mt-8 flex flex-col items-center gap-3">
            {file ? (
              <>
                <a ref={linkRef} href={file} download className="hidden" aria-hidden="true">
                  hidden auto-download
                </a>
                <Button asChild size="lg">
                  <a href={file} download>
                    <Download className="mr-2 h-4 w-4" /> Download the PDF again
                  </a>
                </Button>
                <p className="text-xs text-muted-foreground">
                  Trouble downloading? Check your inbox — the email includes a direct link.
                </p>
              </>
            ) : (
              <>
                <Button asChild size="lg" variant="outline">
                  <Link to="/lead-magnet/security-ops-checklist">Request the checklist again</Link>
                </Button>
                <p className="text-xs text-muted-foreground">
                  This link expired. Enter your email again and we'll resend the PDF.
                </p>
              </>
            )}
          </div>


          <div className="mt-14 rounded-2xl border border-border/60 bg-card p-6 text-left md:p-8">
            <h2 className="text-xl font-semibold">Automate the whole checklist</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              MissionReady is the operations platform behind this checklist — scheduling, dispatch, GPS, patrol
              verification, incident reporting, invoicing, and client reporting in one place. Priced for firms of
              every size, including solo 1099 guards.
            </p>
            <div className="mt-5 flex flex-col gap-3 sm:flex-row">
              <Button asChild>
                <Link to="/signup">Start free 30-day trial</Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/security-guard-management-software">See how it works</Link>
              </Button>
            </div>
          </div>
        </div>
      </PageContainer>
    </MarketingShell>
  );
}
